import { useMemo } from 'react';
import { IShopPreviewProps } from '~/components/ui/ShopPreview';
import { VehicleNumberPlateType, WalletType } from '~/shared';
import { plateTypeList } from '../components/ChangeTab/ChangeTab.model';
import { useLayoutSelector } from '../redux';
import { isCustomType, validateNumberPlate } from '../utils';
import { useBuy } from './useBuy';

const formatNumberWithSpaces = (num: string) => {
	if (num.length !== 6) return num || '';
	return `${num[0]} ${num.slice(1, 4)} ${num.slice(4, 6)}`;
};

const useShopPreview = (): IShopPreviewProps => {
	const { handleBuy, plateNumberPriceCash, plateNumberPriceDonate } = useBuy();
	const selectedTypeIndex = useLayoutSelector(
		(state) => state.plateBuying.selectedTypeIndex,
	);
	const number = useLayoutSelector((state) => state.plateBuying.number);
	const regionCode = useLayoutSelector((state) => state.plateBuying.regionCode);
	const customNumber = useLayoutSelector(
		(state) => state.plateBuying.customNumber,
	);
	const tabIndex = useLayoutSelector((state) => state.plateBuying.tabIndex);

	const selectedType = plateTypeList[selectedTypeIndex];

	const visible = useMemo(() => {
		if (tabIndex !== 0) {
			return false;
		}

		if (isCustomType(selectedType.type)) {
			return !!customNumber && customNumber.length > 0;
		}
		return (
			validateNumberPlate(selectedType.type, number, regionCode) && !!regionCode
		);
	}, [selectedType.type, number, regionCode, customNumber, tabIndex]);

	const name = useMemo(() => {
		if (isCustomType(selectedType.type)) {
			return `Номер ${customNumber || ''}`;
		}

		return number && regionCode
			? `Номер ${formatNumberWithSpaces(number)} ${regionCode}`
			: '';
	}, [selectedType.type, number, regionCode, customNumber]);

	const subtitle = useMemo(() => {
		if (selectedType.type === VehicleNumberPlateType.Classic) {
			return '(Классический)';
		}

		// if (selectedType.type === VehicleNumberPlateType.ClassicSquare) {
		// 	return '(Квадратный)';
		// }

		if (selectedType.type === VehicleNumberPlateType.ClassicNoFlag) {
			return '(Классический без флага)';
		}

		if (selectedType.type === VehicleNumberPlateType.ClassicSquareNoFlag) {
			return '(Квадратный без флага)';
		}

		if (selectedType.type === VehicleNumberPlateType.Custom) {
			return '(Именной белый)';
		}

		if (selectedType.type === VehicleNumberPlateType.CustomBlack) {
			return '(Именной черный)';
		}

		return '';
	}, [selectedType.type]);

	const price = useMemo(() => {
		if (isCustomType(selectedType.type)) {
			return plateNumberPriceDonate?.price || 0;
		}
		return plateNumberPriceCash?.price || 0;
	}, [selectedType.type, plateNumberPriceCash, plateNumberPriceDonate]);

	if (isCustomType(selectedType.type)) {
		return {
			visible,
			name,
			subtitle,
			price,
			handlePayByDonate: () => handleBuy(WalletType.Donate),
			walletType: WalletType.Donate,
		};
	}

	return {
		visible,
		name,
		subtitle,
		price,
		handlePayByCash: () => handleBuy(WalletType.Cash),
		handlePayByCard: () => handleBuy(WalletType.Bank),
		handlePayByDonate: () => handleBuy(WalletType.Donate),
		bottomTitle:
			'После покупки, номер сразу вешается на автомобиль, а старый утилизируется.\n\nЕсли вы хотите сохранить ваш гос. номер, то перейдите во вкладу “Хранилище”.',
	};
};

export { useShopPreview };
